import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Yoybuy Sheet: Полное руководство по поиску дешёвых товаров из Китая | Yoybuy Finds",
  "author": {
    "@type": "Organization",
    "name": "Yoybuy Finds"
  },
  "datePublished": "2026-07-05",
  "dateModified": "2026-07-05",
  "description": "Полное руководство по использованию Yoybuy Sheet — таблицы для поиска дешёвых товаров из Китая. Экономьте до 70% на покупках с проверенными spreadsheet-таблицами.",
  "inLanguage": "ru",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://yoybuyfinds.lat/yoybuy-sheet-guide"
  },
  "image": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg",
  "publisher": {
    "@type": "Organization",
    "name": "Yoybuy Finds",
    "logo": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg"
    }
  }
};

export default function ArticlePage() {
  const { t } = useTranslation();

  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20">
        <article className="py-10 md:py-14">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav aria-label="Breadcrumb">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-foreground-600 text-sm hover:text-accent-500 transition-colors mb-8 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('article.back_home')}
                </Link>
              </nav>

              <header>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-8">
                  {t('article.title')}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-foreground-400 text-xs mb-8">
                  <time dateTime="2026-07-05">5 июля 2026</time>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>Yoybuy Finds</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>10 мин чтения</span>
                </div>
              </header>

              <div className="prose-custom text-foreground-700 leading-relaxed space-y-6">
                <p className="text-lg text-foreground-600">
                  {t('article.intro')}
                </p>

                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    Что такое Yoybuy Sheet и как он работает
                  </h2>
                  <p>
                    Yoybuy sheet — это структурированная таблица в формате spreadsheet, которая содержит прямые ссылки на лучшие товары с китайских маркетплейсов: <strong>Taobao</strong>, <strong>1688</strong> и <strong>Weidian</strong>. Каждая строка таблицы — это проверенный товар с указанием цены, размера, рейтинга продавца и прямой ссылки на покупку. В отличие от обычного поиска через AliExpress, yoybuy sheet позволяет находить те же товары по цене в 2-4 раза дешевле, потому что вы получаете доступ к заводским ценам без посредников.
                  </p>
                  <p>
                    Таблица обновляется ежедневно — команда yoybuy sheet вручную проверяет каждого продавца, отслеживает актуальность цен и добавляет новые трендовые позиции. Все ссылки безопасны и ведут на проверенные магазины с высоким рейтингом и реальными отзывами.
                  </p>
                </section>

                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    Почему Yoybuy Sheet выгоднее AliExpress
                  </h2>
                  <div className="overflow-x-auto my-6">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-background-300">
                          <th className="text-left py-3 px-4 font-semibold text-foreground-950">Платформа</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-950">Цена</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-950">Скорость поиска</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-950">Качество проверки</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-background-200">
                          <td className="py-3 px-4 text-foreground-600">AliExpress</td>
                          <td className="py-3 px-4 text-foreground-600">Высокая</td>
                          <td className="py-3 px-4 text-foreground-600">Средняя</td>
                          <td className="py-3 px-4 text-foreground-600">Автоматическая</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 px-4 font-semibold text-foreground-950">Yoybuy Spreadsheet</td>
                          <td className="py-3 px-4 font-semibold text-accent-600">Низкая (-70%)</td>
                          <td className="py-3 px-4 font-semibold text-foreground-950">Мгновенная</td>
                          <td className="py-3 px-4 font-semibold text-foreground-950">Ручная проверка</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    AliExpress добавляет комиссию за посредничество, рекламу и международную логистику. С yoybuy sheet вы получаете прямые ссылки на фабрики и оптовых продавцов, что позволяет покупать те же товары по заводской цене. Разница может достигать 70% от розничной стоимости.
                  </p>
                </section>

                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    Какие категории товаров доступны
                  </h2>
                  <p>
                    Через yoybuy sheet вы можете найти товары в 14 категориях: обувь, худи и свитера, футболки, куртки, штаны и шорты, головные уборы, комплекты одежды, нижнее бельё, джерси, аксессуары, сумки, ремни, парфюмерия и другие товары. Каждая категория содержит от 200 до 500 проверенных позиций с ценами от $3.
                  </p>
                  <p>
                    Особой популярностью пользуются категории обуви и аксессуаров — здесь вы найдёте копии брендовых моделей по цене в 5-10 раз дешевле оригиналов. Качество при этом остаётся на высоком уровне благодаря тщательной проверке каждого продавца.
                  </p>
                </section>

                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    Как начать использовать Yoybuy Sheet
                  </h2>
                  <ol className="list-decimal list-inside space-y-3 ml-2">
                    <li className="text-foreground-700">
                      <strong>Откройте таблицу</strong> — перейдите на <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-500 hover:text-accent-600 underline">kaspreadsheet.com</a> и выберите интересующую категорию
                    </li>
                    <li className="text-foreground-700">
                      <strong>Выберите товары</strong> — просмотрите отфильтрованные позиции с ценами и фотографиями
                    </li>
                    <li className="text-foreground-700">
                      <strong>Перейдите по ссылке</strong> — каждая позиция содержит прямую ссылку на товар на Taobao, 1688 или Weidian
                    </li>
                    <li className="text-foreground-700">
                      <strong>Оформите заказ</strong> — используйте удобный для вас способ доставки из Китая
                    </li>
                    <li className="text-foreground-700">
                      <strong>Экономьте до 70%</strong> — наслаждайтесь покупками по заводским ценам без переплат
                    </li>
                  </ol>
                </section>

                <section>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    Часто задаваемые вопросы
                  </h2>

                  <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
                    <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                      <h3 className="font-semibold text-foreground-950 mb-2" itemProp="name">
                        Безопасно ли покупать через yoybuy sheet?
                      </h3>
                      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                        <p className="text-foreground-600" itemProp="text">
                          Да, абсолютно безопасно. Все продавцы в таблице проходят ручную проверку — мы анализируем рейтинг, количество продаж, отзывы реальных покупателей и историю магазина. В таблицу попадают только надёжные продавцы с рейтингом выше 4.5 звёзд.
                        </p>
                      </div>
                    </div>

                    <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                      <h3 className="font-semibold text-foreground-950 mb-2" itemProp="name">
                        Как часто обновляется таблица?
                      </h3>
                      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                        <p className="text-foreground-600" itemProp="text">
                          Spreadsheet обновляется ежедневно. Мы отслеживаем изменение цен, появление новых трендовых товаров и удаляем позиции, которые потеряли актуальность. Каждое утро вы получаете свежую подборку лучших предложений.
                        </p>
                      </div>
                    </div>

                    <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                      <h3 className="font-semibold text-foreground-950 mb-2" itemProp="name">
                        Нужно ли платить за доступ к таблице?
                      </h3>
                      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                        <p className="text-foreground-600" itemProp="text">
                          Базовый доступ к yoybuy sheet предоставляется бесплатно. Вы можете просматривать все категории и переходить по ссылкам на товары без каких-либо ограничений и скрытых комиссий.
                        </p>
                      </div>
                    </div>

                    <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                      <h3 className="font-semibold text-foreground-950 mb-2" itemProp="name">
                        Как осуществляется доставка товаров?
                      </h3>
                      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                        <p className="text-foreground-600" itemProp="text">
                          Доставка осуществляется через стандартные каналы международной логистики. Вы можете выбрать удобный для вас способ: прямая доставка продавцом, через карго-сервисы или через агентов по закупкам в Китае. Таблица содержит товары, которые доступны для международной отправки.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mt-12 pt-8 border-t border-background-200">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">
                    Заключение
                  </h2>
                  <p>
                    Yoybuy sheet — это настоящий прорыв в мире онлайн-шопинга из Китая. Вместо того чтобы тратить часы на поиск товаров и проверку продавцов, вы получаете готовую, ежедневно обновляемую таблицу с лучшими предложениями. Экономия до 70%, безопасные ссылки и удобная навигация по категориям делают yoybuy sheet незаменимым инструментом для тех, кто ценит своё время и деньги.
                  </p>
                  <p className="font-semibold">
                    Начните экономить уже сегодня — переходите на <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-500 hover:text-accent-600 underline">kaspreadsheet.com</a> и откройте для себя мир выгодных покупок!
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('article.back_home')}
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}